/**
 * Remember which maneuver baseline (and config, and colour) the user is displaying.
 *
 * Same two-tier scheme as performanceBaselinePersistence: sessionStorage for the current tab,
 * persistent settings across sessions, session winning on restore because it is the most recent
 * deliberate choice. "No baseline" is stored as an explicit record with a null id, so turning the
 * baseline off sticks instead of one being auto-selected on the next visit.
 *
 * Separate keys from the performance slots on purpose: the two pickers list different baselines
 * (kind='maneuver' vs kind='straight_line'), so sharing a key would restore an id the other page
 * cannot draw. There is one maneuver slot -- the maneuver views draw one reference curve per chart,
 * and a second would double every overlay on the map and timeseries too.
 */

import { persistentSettingsService } from '../services/persistentSettingsService';
import { cleanQuotes } from './global';
import { debug as logDebug } from './console';
import { normalizePerformanceTargetColor } from './performanceTargetColor';

const SESSION_STORAGE_KEY = 'maneuverBaseline';

const SETTING_KEYS = {
  id: 'maneuverBaselineId',
  name: 'maneuverBaselineName',
  config: 'maneuverBaselineConfig',
  color: 'maneuverBaselineColor',
} as const;

export interface ManeuverBaselineSelection {
  /** null means an explicit "No baseline". */
  baselineId: number | null;
  baselineName: string;
  baselineConfig: string;
  baselineColor?: string;
}

export function readManeuverBaselineSession(): Partial<ManeuverBaselineSelection> | null {
  try {
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) { return null; }
    const parsed = JSON.parse(raw) as Partial<ManeuverBaselineSelection>;
    const id = Number(parsed.baselineId);
    return {
      baselineId: Number.isInteger(id) && id > 0 ? id : null,
      baselineName: typeof parsed.baselineName === 'string' ? parsed.baselineName : '',
      baselineConfig: typeof parsed.baselineConfig === 'string' ? parsed.baselineConfig : '',
      baselineColor: typeof parsed.baselineColor === 'string'
        ? normalizePerformanceTargetColor(parsed.baselineColor)
        : undefined,
    };
  } catch {
    return null;
  }
}

export function writeManeuverBaselineSession(partial: Partial<ManeuverBaselineSelection>): void {
  try {
    const existing = readManeuverBaselineSession() || {};
    const next = { ...existing, ...partial };
    if (partial.baselineColor !== undefined) {
      next.baselineColor = normalizePerformanceTargetColor(partial.baselineColor);
    }
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(next));
  } catch (error) {
    logDebug('maneuverBaselinePersistence: Error writing sessionStorage', error);
  }
}

/**
 * The saved selection, or null when this user has never chosen on this project.
 *
 * null means "no opinion", so the caller may auto-select; a record with `baselineId: null` means
 * the user turned the baseline off and it must stay off.
 */
export async function loadManeuverBaselineFromPersistentSettings(
  userId: string,
  className: string,
  projectId: number
): Promise<ManeuverBaselineSelection | null> {
  try {
    const settings = await persistentSettingsService.loadSettings(userId, className, projectId);
    if (!settings) { return null; }

    const hasKeys = SETTING_KEYS.id in settings
      || SETTING_KEYS.name in settings
      || SETTING_KEYS.config in settings;
    if (!hasKeys) { return null; }

    const rawId = cleanQuotes(String(settings[SETTING_KEYS.id] ?? '')).trim();
    const id = Number(rawId);
    return {
      baselineId: rawId !== '' && Number.isInteger(id) && id > 0 ? id : null,
      baselineName: cleanQuotes(String(settings[SETTING_KEYS.name] ?? '')).trim(),
      baselineConfig: cleanQuotes(String(settings[SETTING_KEYS.config] ?? '')).trim(),
      baselineColor: settings[SETTING_KEYS.color]
        ? normalizePerformanceTargetColor(cleanQuotes(String(settings[SETTING_KEYS.color])))
        : undefined,
    };
  } catch (error) {
    logDebug('maneuverBaselinePersistence: Error loading from persistent settings', error);
    return null;
  }
}

export async function saveManeuverBaselineToPersistentSettings(
  userId: string,
  className: string,
  projectId: number,
  partial: Partial<ManeuverBaselineSelection>
): Promise<void> {
  const payload: Record<string, string> = {};
  if (partial.baselineId !== undefined) {
    payload[SETTING_KEYS.id] = partial.baselineId == null ? '' : String(partial.baselineId);
  }
  if (partial.baselineName !== undefined) { payload[SETTING_KEYS.name] = partial.baselineName; }
  if (partial.baselineConfig !== undefined) { payload[SETTING_KEYS.config] = partial.baselineConfig; }
  if (partial.baselineColor !== undefined) {
    payload[SETTING_KEYS.color] = normalizePerformanceTargetColor(partial.baselineColor);
  }
  if (Object.keys(payload).length === 0) { return; }

  await persistentSettingsService.saveSettings(userId, className, projectId, payload);
  logDebug('maneuverBaselinePersistence: Saved baseline settings', payload);
}
